// Main container. Composes the three visual regions the operator sees:
//   1. Empty state (welcome + suggestions) OR message list
//   2. Pending-confirmation cards (rendered above the input for
//      visibility — the operator's last decision is what unblocks the
//      chat)
//   3. Chat input
//
// Composes the hook (useAgentChat) which owns state, SSE, and
// persistence. This component is pure presentation + event routing.

import { useCallback, useMemo, useState } from 'react';
import { Box, Button, HStack, Heading, Text, VStack } from '@chakra-ui/react';
import { planBriefingEnabled } from './briefing';
import { useAgentChat } from './useAgentChat';
import { useAgentContext } from './AgentContextProvider';
import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput';
import { ConfirmationCard } from './ConfirmationCard';
import type { ChatAttachment, ChatAttachmentPreview, ChatContext } from './types';

type Props = {
  brandName?: string | null;
  brandId?:   string | null;
  // Explicit context override. When omitted (the common case) the
  // component reads from AgentContextProvider — which merges the
  // active brand, URL-derived ids, and any imperative page pushes.
  // Explicit prop takes precedence for callers that need to hard-pin
  // (e.g. an embed inside a debug view).
  context?:   ChatContext;
  // Optional starter prompts — the empty state renders these as
  // clickable chips. If omitted, we synthesise a reasonable default.
  suggestions?: string[];
  // When true, the chat fills its parent container's height instead
  // of using the built-in fixed height. Parent must set a height
  // (e.g. via calc(100vh - Xrem)) or the flex layout collapses to 0.
  fill?: boolean;
  // Plan-studio lifestyle handoff. A proposed plan whose dollar estimate
  // is above this cap cannot be confirmed. Absent on a normal visit.
  spendCapUsd?: number | null;
  // Catalog-image suggestions also put creativeType, planId, and capUsd
  // on the chat request and on the lifestyle tool call.
  planId?: string | null;
  creativeType?: string | null;
};

function getDefaultSuggestions(brandName?: string | null): string[] {
  const subject = brandName || 'my account';
  return [
    `What's worth my attention on ${subject} today?`,
    'Show me the most recent 5 ads',
    'Which products need reviews refreshed?',
    'Regenerate an ad with a new prompt'
  ];
}

export function AgentChat({
  brandName, brandId, context: contextOverride, suggestions, fill, spendCapUsd = null, planId = null, creativeType = null
}: Props) {
  const ambient = useAgentContext();
  // Explicit prop wins; otherwise use the ambient (provider-supplied)
  // context. The chat re-renders when ambient changes, so the
  // operator navigating from /home to /campaigns/:id automatically
  // starts sending campaignId on the next turn without any component
  // remount. A catalog-image plan link adds the fields the lifestyle
  // tool call has to carry.
  const context = useMemo(() => {
    const base: ChatContext = { ...(contextOverride || ambient) };
    if (creativeType === 'catalog-image') {
      base.creativeType = 'catalog-image';
      if (planId) base.planId = planId;
      if (typeof spendCapUsd === 'number' && Number.isFinite(spendCapUsd)) base.capUsd = spendCapUsd;
    }
    return base;
  }, [contextOverride, ambient, creativeType, planId, spendCapUsd]);
  const chat = useAgentChat({ brandId, context, briefing: planBriefingEnabled() });
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [previews, setPreviews] = useState<ChatAttachmentPreview[]>([]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if ((!text && attachments.length === 0) || chat.sending) return;
    setDraft('');
    const atts = attachments;
    const prevs = previews;
    setAttachments([]);
    setPreviews([]);
    await chat.send(text, atts, prevs);
  }, [draft, attachments, previews, chat]);

  const onAttachmentsChange = useCallback(
    (atts: ChatAttachment[], prevs: ChatAttachmentPreview[]) => {
      setAttachments(atts);
      setPreviews(prevs);
    },
    [],
  );

  const useSuggestion = useCallback(async (text: string) => {
    if (chat.sending) return;
    await chat.send(text);
  }, [chat]);

  const suggestionList = suggestions && suggestions.length ? suggestions : getDefaultSuggestions(brandName);

  return (
    <VStack align="stretch" spacing={3} h={fill ? '100%' : { base: '520px', md: '640px' }}>
      {/* ── Header row with New chat button ── */}
      <HStack justify="space-between" px={{ base: 3, md: 0 }}>
        <Heading size="sm">
          {brandName ? `Ask about ${brandName}` : 'Ask the operator agent'}
        </Heading>
        {chat.turns.length > 0 && (
          <Button size="xs" variant="ghost" onClick={chat.clear}>
            New chat
          </Button>
        )}
      </HStack>

      {/* ── Message list OR empty state ── */}
      {chat.turns.length === 0
        ? chat.briefingLoading
          ? <Text fontSize="sm" color="brand.muted" px={4} data-testid="briefing-loading">Loading the plan briefing…</Text>
          : <EmptyState brandName={brandName} suggestions={suggestionList} onPick={useSuggestion} disabled={chat.sending} />
        : <MessageList turns={chat.turns} />
      }

      {/* ── Pending confirmations (rendered above input for visibility) ── */}
      {chat.pendingActions.length > 0 && (
        <VStack align="stretch" spacing={2} maxW="760px" mx="auto" w="100%" px={{ base: 3, md: 0 }}>
          {chat.pendingActions.map((a) => (
            <ConfirmationCard
              key={a.toolCallId}
              action={a}
              busy={chat.sending}
              spendCapUsd={spendCapUsd}
              brandId={brandId || context.brandId || null}
              activeBrandId={context.brandId || null}
              onConfirm={(id, phrase) => chat.confirm([id], phrase ? { [id]: phrase } : {})}
              onCancel={(id) => chat.cancel([id])}
            />
          ))}
        </VStack>
      )}

      {/* ── Error banner ── */}
      {chat.error && !chat.streaming && (
        <Box maxW="760px" mx="auto" w="100%" p={2} borderWidth="1px" borderColor="red.300" bg="red.50" borderRadius="md">
          <Text fontSize="12px" color="red.700">{chat.error}</Text>
        </Box>
      )}

      {/* ── Input row ── */}
      <Box maxW="760px" mx="auto" w="100%" px={{ base: 3, md: 0 }}>
        <ChatInput
          value={draft}
          onChange={setDraft}
          attachments={attachments}
          previews={previews}
          onAttachmentsChange={onAttachmentsChange}
          onSend={send}
          onAbort={chat.abort}
          disabled={chat.sending}
          busy={chat.streaming}
          placeholder={brandName ? `Ask about ${brandName}…` : 'Ask about your ads, catalog, spend…'}
        />
        <Text fontSize="10px" color="brand.muted" textAlign="center" mt={1}>
          The agent can inspect, edit, and orchestrate — high-impact actions require your explicit confirmation.
        </Text>
      </Box>
    </VStack>
  );
}

function EmptyState({ brandName, suggestions, onPick, disabled }: { brandName?: string | null; suggestions: string[]; onPick: (t: string) => void; disabled: boolean }) {
  return (
    <VStack spacing={6} flex="1" justify="center" px={4} py={8}>
      <VStack spacing={2}>
        <Heading size="md" textAlign="center">
          One chat, everything you need for{' '}
          <Box as="span" fontWeight="bold">{brandName || 'your account'}</Box>.
        </Heading>
        <Text fontSize="13px" color="brand.muted" textAlign="center" maxW="560px">
          Ask in plain English — I'll interpret, do the work, and confirm anything billable or external. I'll also flag things I think you should look at.
        </Text>
      </VStack>
      <VStack spacing={2} maxW="560px" w="100%">
        {suggestions.map((s) => (
          <Button
            key={s}
            variant="outline"
            size="sm"
            w="100%"
            justifyContent="flex-start"
            fontWeight="400"
            onClick={() => onPick(s)}
            isDisabled={disabled}
          >
            {s}
          </Button>
        ))}
      </VStack>
    </VStack>
  );
}
